import { BackgroundAccessors } from '../Background';
import { ClassAccessors } from '../Class';
import { ClassFeatureAccessors } from '../ClassFeature';
import { ConditionAccessors } from '../Condition';
import { DataOriginTypeEnum, DataOriginUtils } from '../DataOrigin';
import { FeatAccessors } from '../Feat';
import { ItemAccessors } from '../Item';
import { RaceAccessors } from '../Race';
import { RacialTraitAccessors } from '../RacialTrait';
import { VehicleAccessors } from '../Vehicle';
import { validateIsRestrictedEntityDefinition } from './validators';
/**
 *
 * @param dataOrigin
 * @param defaultValue
 * @param tryParent
 */
export function getDataOriginName(dataOrigin, defaultValue = '', tryParent = false) {
    let extraDisplay = null;
    switch (dataOrigin.type) {
        case DataOriginTypeEnum.RACE:
            if (tryParent) {
                extraDisplay = RaceAccessors.getFullName(dataOrigin.parent);
            }
            if (extraDisplay === null) {
                extraDisplay = RacialTraitAccessors.getName(dataOrigin.primary);
            }
            break;
        case DataOriginTypeEnum.CLASS:
            extraDisplay = ClassAccessors.getName(dataOrigin.primary);
            break;
        case DataOriginTypeEnum.CLASS_FEATURE:
            if (tryParent) {
                extraDisplay = ClassAccessors.getName(dataOrigin.parent);
            }
            if (extraDisplay === null) {
                extraDisplay = ClassFeatureAccessors.getName(dataOrigin.primary);
            }
            break;
        case DataOriginTypeEnum.FEAT:
            extraDisplay = FeatAccessors.getName(dataOrigin.primary);
            break;
        case DataOriginTypeEnum.BACKGROUND:
            extraDisplay = BackgroundAccessors.getName(dataOrigin.primary);
            break;
        case DataOriginTypeEnum.ITEM:
            extraDisplay = ItemAccessors.getDefinitionName(dataOrigin.primary);
            break;
        case DataOriginTypeEnum.CUSTOM:
            extraDisplay = dataOrigin.primary === null ? null : dataOrigin.primary;
            break;
        default:
        //not implemented
    }
    return extraDisplay === null ? defaultValue : extraDisplay;
}
/**
 *
 * @param ref
 * @param refData
 * @param defaultValue
 * @param tryParent
 */
export function getDataOriginRefName(ref, refData, defaultValue, tryParent = false) {
    var _a;
    let extraDisplay = null;
    switch (ref.type) {
        case DataOriginTypeEnum.RACE: {
            if (tryParent) {
                const parent = DataOriginUtils.getRefParent(ref, refData);
                if (parent !== null) {
                    extraDisplay = RaceAccessors.getFullName(parent);
                }
            }
            if (extraDisplay === null) {
                const primary = DataOriginUtils.getRefPrimary(ref, refData);
                if (primary !== null) {
                    extraDisplay = RacialTraitAccessors.getName(primary);
                }
            }
            break;
        }
        case DataOriginTypeEnum.CLASS: {
            const primary = DataOriginUtils.getRefPrimary(ref, refData);
            if (primary) {
                extraDisplay = ClassAccessors.getName(primary);
            }
            break;
        }
        case DataOriginTypeEnum.CLASS_FEATURE: {
            if (tryParent) {
                const parent = DataOriginUtils.getRefParent(ref, refData);
                if (parent !== null) {
                    extraDisplay = ClassAccessors.getName(parent);
                }
            }
            if (extraDisplay === null) {
                const primary = DataOriginUtils.getRefPrimary(ref, refData);
                if (primary !== null) {
                    extraDisplay = ClassFeatureAccessors.getName(primary);
                }
            }
            break;
        }
        case DataOriginTypeEnum.FEAT: {
            const primary = DataOriginUtils.getRefPrimary(ref, refData);
            if (primary !== null) {
                extraDisplay = FeatAccessors.getName(primary);
            }
            break;
        }
        case DataOriginTypeEnum.BACKGROUND: {
            const primary = DataOriginUtils.getRefPrimary(ref, refData);
            if (primary !== null) {
                extraDisplay = BackgroundAccessors.getName(primary);
            }
            break;
        }
        case DataOriginTypeEnum.ITEM: {
            const primary = DataOriginUtils.getRefPrimary(ref, refData);
            if (primary !== null) {
                extraDisplay = ItemAccessors.getName(primary);
            }
            break;
        }
        case DataOriginTypeEnum.CUSTOM: {
            extraDisplay = 'Custom';
            break;
        }
        case DataOriginTypeEnum.UNKNOWN: {
            extraDisplay = defaultValue !== null && defaultValue !== void 0 ? defaultValue : 'No Origin';
            break;
        }
        default:
        //not implemented
    }
    return (_a = extraDisplay !== null && extraDisplay !== void 0 ? extraDisplay : defaultValue) !== null && _a !== void 0 ? _a : '';
}
/**
 *
 * @param dataOrigin
 * @param fallback
 */
export function getPrimaryDescription(dataOrigin, fallback = '') {
    let description = fallback;
    if (!dataOrigin) {
        return description;
    }
    switch (dataOrigin.type) {
        case DataOriginTypeEnum.RACE:
            description = RacialTraitAccessors.getDescription(dataOrigin.primary);
            break;
        case DataOriginTypeEnum.FEAT:
            const featDescription = FeatAccessors.getDescription(dataOrigin.primary);
            description = featDescription === null ? '' : featDescription;
            break;
        case DataOriginTypeEnum.CLASS_FEATURE:
            const classFeatureDescription = ClassFeatureAccessors.getDescription(dataOrigin.primary);
            description = classFeatureDescription === null ? '' : classFeatureDescription;
            break;
        default:
            if (dataOrigin.primary && dataOrigin.primary.definition && dataOrigin.primary.definition.description) {
                description = dataOrigin.primary.definition.description;
            }
    }
    return description === null ? '' : description;
}
/**
 *
 * @param ref
 * @param refData
 * @param fallback
 */
export function getDataOriginRefPrimaryDescription(ref, refData, fallback = '') {
    let description = null;
    switch (ref.type) {
        case DataOriginTypeEnum.RACE: {
            const primary = DataOriginUtils.getRefPrimary(ref, refData);
            if (primary !== null) {
                description = RacialTraitAccessors.getDescription(primary);
            }
            break;
        }
        case DataOriginTypeEnum.FEAT: {
            const primary = DataOriginUtils.getRefPrimary(ref, refData);
            if (primary !== null) {
                description = FeatAccessors.getDescription(primary);
            }
            break;
        }
        case DataOriginTypeEnum.CLASS: {
            const primary = DataOriginUtils.getRefPrimary(ref, refData);
            if (primary) {
                description = ClassAccessors.getDescription(primary);
            }
            break;
        }
        case DataOriginTypeEnum.CLASS_FEATURE: {
            const primary = DataOriginUtils.getRefPrimary(ref, refData);
            if (primary !== null) {
                description = ClassFeatureAccessors.getDescription(primary);
            }
            break;
        }
        case DataOriginTypeEnum.BACKGROUND: {
            const primary = DataOriginUtils.getRefPrimary(ref, refData);
            if (primary !== null) {
                description = BackgroundAccessors.getDescription(primary);
            }
            break;
        }
        case DataOriginTypeEnum.ITEM: {
            const primary = DataOriginUtils.getRefPrimary(ref, refData);
            if (primary !== null) {
                description = ItemAccessors.getDescription(primary);
            }
            break;
        }
        case DataOriginTypeEnum.VEHICLE: {
            const primary = DataOriginUtils.getRefPrimary(ref, refData);
            if (primary !== null) {
                description = VehicleAccessors.getDescription(primary);
            }
            break;
        }
        case DataOriginTypeEnum.CONDITION: {
            const primary = DataOriginUtils.getRefPrimary(ref, refData);
            if (primary !== null) {
                description = ConditionAccessors.getDescription(primary);
            }
            break;
        }
        default:
        // not implemented
    }
    return description !== null && description !== void 0 ? description : fallback;
}
/**
 *
 * @param entities
 * @param entityRestrictionData
 */
export function filterNonRestrictedEntities(entities, entityRestrictionData) {
    return entities.filter((entity) => entity.definition !== null &&
        !validateIsRestrictedEntityDefinition(entity.definition, entityRestrictionData));
}
/**
 *
 * @param entityDefinitions
 * @param entityRestrictionData
 */
export function filterNonRestrictedEntityDefinitions(entityDefinitions, entityRestrictionData) {
    return entityDefinitions.filter((definition) => !validateIsRestrictedEntityDefinition(definition, entityRestrictionData));
}
